import { useCallback, useEffect, useRef } from 'react';

import { useSendEventsMutation } from '../datasource/mutations/eventHub';
import { createSessionContextEvent, getGaIds, getNormalizedPageTitle, getRawGaSessionCookie, getUID, syntheticEventHubEvent } from '../utility/dataLayerUtils';
import logger from '../utility/logger';
import { useAppSelector } from '.';

const intervalInSeconds = 5;
const SESSION_CONTEXT_SENT_KEY = '_pli_session_context_sent';

// GTM system events can carry DOM elements (e.g. gtm.element on gtm.formInteract);
// React-managed nodes make JSON.stringify throw on circular fiber references, which
// would poison the whole batch. Serialize defensively instead.
const safeStringifyEvent = (event: object) => {
    const seen = new WeakSet();
    return JSON.stringify(event, (key, value) => {
        if (value && typeof value === 'object') {
            if (typeof Node !== 'undefined' && value instanceof Node) {
                const element = value as Element;
                return element.tagName
                    ? element.tagName.toLowerCase() + (element.id ? `#${element.id}` : '')
                    : '[dom]';
            }
            if (seen.has(value)) return '[circular]';
            seen.add(value);
        }
        return typeof value === 'function' ? undefined : value;
    });
};

// Send events to EventHubs reading window.eventHubDataLayer array every N seconds
export const useEventHubDataLayerConsumer = () => {
    const lastIndexProcessed = useRef<number>(0);
    const sessionContextSent = useRef(false);
    const pageLoadTime = useRef(Date.now());
    const failedFlushAttempts = useRef(0);
    const deliveryStallReported = useRef(false);

    // Delivery watchdog: a backlog that never drains makes every downstream failure
    // mode visible (API down, network blocked, ...). Reported once per stall.
    const reportDeliveryStallIfNeeded = useCallback((backlog: number) => {
        if (deliveryStallReported.current) return;
        if (failedFlushAttempts.current < 3 && backlog < 50) return;

        deliveryStallReported.current = true;
        logger.error('eventhub_delivery_stalled', {
            backlog: backlog,
            failedFlushAttempts: failedFlushAttempts.current
        }, true);
    }, []);

    const [sendEvents] = useSendEventsMutation();
    const user = useAppSelector((state) => state.user);
    const userId = user?.individualSK.toString()?.length > 0 ? user.individualSK.toString() : '000000';
    const clientConfig = useAppSelector((state) => state.clientConfig);
    const measurementId = clientConfig?.ga4MeasurementId;

    // Cookies are read per call: GA sets them asynchronously after load, so values
    // captured once at mount could stay 'unknown' for the whole session.
    const getSessionInfo = useCallback(() => {
        const gaCookie = getRawGaSessionCookie(measurementId);
        const { clientId, sessionId, sessionNumber } = getGaIds(measurementId);

        return {
            clientId,
            sessionId,
            sessionNumber,
            sessionGuid: gaCookie ? `${gaCookie}.${userId}` : 'NoSession'
        };
    }, [measurementId, userId]);

    const sendSessionContext = useCallback(() => {
        if (!clientConfig) {
            return; // wait for the measurement id so GA identifiers are parsed correctly
        }

        const { sessionGuid } = getSessionInfo();
        const sessionStorageKey = `${SESSION_CONTEXT_SENT_KEY}_${sessionGuid}`;
        const alreadySent = sessionStorage.getItem(sessionStorageKey);

        if (!alreadySent && !sessionContextSent.current) {
            // Queue through the shared push path so the event gets the page snapshot and
            // unique id, and is sent exactly once by processEvents (a direct send here
            // would duplicate it on the first flush).
            syntheticEventHubEvent(createSessionContextEvent(measurementId));
            sessionStorage.setItem(sessionStorageKey, 'true');
            sessionContextSent.current = true;
        }
    }, [clientConfig, getSessionInfo, measurementId]);

    const processEvents = useCallback(async () => {
        if (!clientConfig) {
            return;
        }

        const currentLastIndex = window.eventHubDataLayer?.length ?? 0;
        if (currentLastIndex <= lastIndexProcessed.current) {
            return;
        }

        const eventsToBeProcessed = window.eventHubDataLayer.slice(lastIndexProcessed.current, currentLastIndex);

        try {
            const { clientId, sessionId, sessionNumber, sessionGuid } = getSessionInfo();
            const analyticsEventHash = getUID();
            const analyticsSentEventTimestamp = new Date().getTime();

            const jsonEventsToBeProcessed = eventsToBeProcessed.map((event, index) => {
                const enrichedEvent = {
                    ...event,
                    client_id: clientId || 'unknown',
                    ga_session_id: sessionId || 'unknown',
                    ga_session_number: sessionNumber?.toString() || '1',
                    analytics_event_hash: analyticsEventHash,
                    analytics_sent_event_timestamp: analyticsSentEventTimestamp,
                    sessionGuid: sessionGuid,
                    // Push-time values win; flush-time values are the fallback for events
                    // that only arrive via the index.html dataLayer mirror
                    engagement_time_msec: event.engagement_time_msec ?? Date.now() - pageLoadTime.current,
                    page_location: event.page_location ?? window.location.href,
                    page_referrer: event.page_referrer ?? document.referrer,
                    page_title: event.page_title ?? getNormalizedPageTitle(),
                    dataSource: 'Watch'
                };

                window.eventHubDataLayer[lastIndexProcessed.current + index] = enrichedEvent;

                return safeStringifyEvent(enrichedEvent);
            });

            const response = await sendEvents(jsonEventsToBeProcessed).unwrap();
            if (response.dataSentOK) {
                lastIndexProcessed.current = currentLastIndex;
                failedFlushAttempts.current = 0;
                deliveryStallReported.current = false;
                return;
            }
            failedFlushAttempts.current += 1;
            reportDeliveryStallIfNeeded(eventsToBeProcessed.length);
        } catch (error) {
            // Keep lastIndexProcessed unchanged so the batch is retried on the next flush
            failedFlushAttempts.current += 1;
            logger.error('event hub flush error', { error: error }, false);
            reportDeliveryStallIfNeeded(eventsToBeProcessed.length);
        }
    }, [clientConfig, getSessionInfo, reportDeliveryStallIfNeeded, sendEvents]);

    useEffect(() => {
        sendSessionContext();
        processEvents();

        const timerInterval = setInterval(() => {
            processEvents();
        }, intervalInSeconds * 1000);

        // Flush pending events when the page is hidden or unloaded, otherwise events
        // pushed during the last interval window are lost on navigation away.
        const flush = () => {
            processEvents();
        };
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') flush();
        };

        window.addEventListener('pagehide', flush);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            clearInterval(timerInterval);
            window.removeEventListener('pagehide', flush);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [processEvents, sendSessionContext]);

    return {
        processEvents,
    };
};
